import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { ChannelStatusIndicator } from './ChannelStatusIndicator';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface ChannelSelectorProps {
  channels: Array<{
    channel: string;
    name: string;
    isConnected: boolean;
  }>;
  selectedChannels: string[];
  onSelectionChange: (channels: string[]) => void;
  bindings?: Array<{
    channel: string;
    syncStatus: 'pending' | 'synced' | 'error' | 'never_synced' | 'syncing';
  }>;
  multiSelect?: boolean;
  containerStyle?: any;
}

const getChannelIcon = (channel: string): string => {
  switch (channel.toLowerCase()) {
    case 'whatsapp':
      return 'logo-whatsapp';
    case 'facebook':
      return 'logo-facebook';
    case 'telegram':
      return 'paper-plane';
    case 'instagram':
      return 'logo-instagram';
    case 'snapchat':
      return 'camera';
    case 'linkedin':
      return 'logo-linkedin';
    default:
      return 'globe-outline';
  }
};

const getChannelColor = (channel: string): string => {
  switch (channel.toLowerCase()) {
    case 'whatsapp':
      return '#25D366';
    case 'facebook':
      return '#0084FF';
    case 'telegram':
      return '#0088CC';
    case 'instagram':
      return '#E4405F';
    case 'snapchat':
      return '#FFFC00';
    case 'linkedin':
      return '#0077B5';
    default:
      return colors.primary;
  }
};

export const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  channels,
  selectedChannels,
  onSelectionChange,
  bindings = [],
  multiSelect = true,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const getBindingForChannel = (channel: string) => {
    return bindings.find(b => b.channel === channel);
  };

  const handleChannelToggle = (channel: string) => {
    if (!multiSelect) {
      onSelectionChange([channel]);
      return;
    }

    if (selectedChannels.includes(channel)) {
      onSelectionChange(selectedChannels.filter(c => c !== channel));
    } else {
      onSelectionChange([...selectedChannels, channel]);
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <ScrollView 
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {channels.map((ch) => {
          const isSelected = selectedChannels.includes(ch.channel);
          const binding = getBindingForChannel(ch.channel);
          const channelIcon = getChannelIcon(ch.channel);
          const channelColor = getChannelColor(ch.channel);
          const isDisabled = !ch.isConnected;

          return (
            <TouchableOpacity
              key={ch.channel}
              style={[
                styles.channelItem,
                isSelected && styles.channelItemSelected,
                isDisabled && styles.channelItemDisabled,
              ]}
              onPress={() => !isDisabled && handleChannelToggle(ch.channel)}
              disabled={isDisabled}
              activeOpacity={0.7}
            >
              <View style={[styles.iconContainer, { backgroundColor: channelColor + '20' }]}>
                <Ionicons name={channelIcon as any} size={24} color={channelColor} />
              </View>
              
              <View style={styles.infoContainer}>
                <Text style={[styles.channelName, isDisabled && styles.disabledText]}>
                  {ch.name}
                </Text>
                {binding && (
                  <ChannelStatusIndicator status={binding.syncStatus} size="small" />
                )}
                {!ch.isConnected && (
                  <Text style={styles.notConnectedText}>Not Connected</Text>
                )}
              </View>

              <View style={styles.selectionContainer}>
                {isSelected && (
                  <View style={[styles.checkmark, { backgroundColor: channelColor }]}>
                    <Ionicons name="checkmark" size={16} color={colors.white} />
                  </View>
                )}
                {!isSelected && !isDisabled && (
                  <View style={styles.unselectedCircle} />
                )}
                {isDisabled && (
                  <Ionicons name="lock-closed" size={18} color={colors.textLight} />
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    minHeight: 100,
  },
  scrollView: {
    maxHeight: 350,
  },
  scrollContent: {
    paddingBottom: spacing.xs,
  },
  channelItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: withAlpha(colors.border, 0.25),
    ...designTokens.shadows.sm,
  },
  channelItemSelected: {
    borderColor: colors.primary,
    borderWidth: 2,
    backgroundColor: withAlpha(colors.primary, 0.02),
  },
  channelItemDisabled: {
    opacity: 0.6,
    backgroundColor: colors.lightGray,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  infoContainer: {
    flex: 1,
    minWidth: 0,
  },
  channelName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  disabledText: {
    color: colors.textLight,
  },
  notConnectedText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  selectionContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmark: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unselectedCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.border,
  },
});

